import MarkDownPreview from '@components/reuseable/MarkDownPreview';
import AuthorDetails from '@components/shared/bytes/article-card/AuthorDetails';
import DarkLayout from '@components/shared/DarkLayout';
import SiteHeader from '@components/shared/SiteHeader';
import { getBytesStrapiUrl } from '@lib/bytesStrapiServices';
import { getImgUrl } from '@utils/functions/getImgUrl';
import useIntersectionObserver from '@utils/hooks/useIntersectionObserver';
import axios from 'axios';
import moment from 'moment';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { PrimaryBadge } from 'stories/Badge.stories';
import { Faq } from '@components/local/courses/faq';

const ShareButtons = dynamic(() => import('@components/shared/bytes/share-buttons/ShareButtons'), {
  ssr: false,
});

const apiBaseUrl = getBytesStrapiUrl();

function CheatSheetPage({ cheatSheet }) {
  const { metaTitle, metaDescription, canonicalURL, structuredData, metaImage, metaRobots } =
    cheatSheet?.seo || {};

  const [headings, setHeadings] = useState([]);

  const router = useRouter();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const headingElements = Array.from(document.querySelectorAll('h2.content-heading'));
      setHeadings(headingElements.map((h2) => ({ text: h2.innerText, id: h2.id })));
    }

    return () => setHeadings([]);
  }, [router]);

  const currentIntersectId = useIntersectionObserver(
    headings.map(({ id, text }) => id),
    {
      threshold: 1,
    }
  );

  return (
    <>
      <SiteHeader
        image={metaImage?.data?.attributes?.url}
        title={metaTitle}
        desc={metaDescription}
        canonicalUrl={canonicalURL}
        structuredData={structuredData}
        metaRobots={metaRobots}
        authorName={cheatSheet?.author?.data?.attributes?.name}
        publishedAt={cheatSheet.publishedAt}
        updatedAt={cheatSheet.lastUpdatedAt || cheatSheet.updatedAt}
        readingTime={cheatSheet?.readingTime}
      />
      <div>
        <DarkLayout customStyle="gap-x-8 gap-y-10 px-4 pt-12 pb-[65px] md:grid-cols-2 md:px-[124px] md:pb-[146px] md:pt-[98px]">
          <div className="z-1 relative flex flex-col items-start gap-y-[12px] text-white md:gap-y-[16px]">
            {cheatSheet?.tags?.data.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-black">
                {cheatSheet.tags.data.slice(0, 2)?.map((item, i) => (
                  <PrimaryBadge size={'large'} label={item.attributes.name} key={item.id} />
                ))}
              </div>
            )}
            <h1 className="font-satoshi-bold font-satoshi-bold text-[36px] font-bold  leading-10 tracking-wide md:text-[48px] md:leading-tight">
              {cheatSheet.title}
            </h1>
            {cheatSheet && (
              <span className="text-[14px] leading-5">
                Last Updated:{' '}
                {moment(cheatSheet.lastUpdatedAt || cheatSheet.updatedAt).format('Do MMMM, YYYY')}
              </span>
            )}
            <AuthorDetails
              avatarImage={cheatSheet?.author.data?.attributes?.thumbnail}
              name={cheatSheet?.author?.data?.attributes?.name}
              designation={cheatSheet?.author?.data?.attributes?.designation}
            />
            <p className="font-satoshi-regular text-[16px] leading-6 md:text-[18px] md:leading-7">
              {cheatSheet?.bodyPreview}
            </p>
          </div>
          {cheatSheet?.heroImage?.data && (
            <div className="w-full overflow-hidden rounded-xl">
              <Image
                width={725}
                height={403}
                objectFit="cover"
                src={getImgUrl(cheatSheet?.heroImage)}
              />
            </div>
          )}
        </DarkLayout>

        <section className="mx-4 mt-[33px] mb-[41px] grid scroll-smooth md:mx-[160px] md:mt-[72px] md:mb-[81px] md:grid-cols-12">
          <div className={`sticky top-8 hidden place-self-start lg:col-span-3 lg:block`}>
            {headings.length > 0 && (
              <div
                className={
                  'mb-[40px] flex w-full flex-col items-start gap-y-[24px] border-y border-gray-2 bg-white py-[40px]'
                }
              >
                <p className="flex items-center pb-2 font-satoshi-bold text-[16px] capitalize leading-6 text-[#646464]">
                  Table of contents
                </p>

                {headings?.map((item) => (
                  <>
                    <p
                      key={item.text}
                      className={`${
                        item.id === currentIntersectId ? 'text-red' : 'text-[#646464]'
                      } cursor-pointer font-satoshi-medium text-[16px] capitalize leading-6`}
                      onClick={() => {
                        document.getElementById(item.id)?.scrollIntoView({});
                        window.location.hash = `#${item.id}`;
                      }}
                    >
                      {item.text}
                    </p>
                  </>
                ))}
              </div>
            )}
            <ShareButtons grayout />
          </div>

          <div className="md:col-span-12 lg:col-span-8 lg:col-start-5">
            <MarkDownPreview content={cheatSheet?.body} />
          </div>
        </section>

        {cheatSheet?.faq.length > 0 && (
          <div className="flex items-center justify-center bg-[#FCFCFD] py-[64px] px-[16px] md:py-[96px] md:px-[124px]">
            <div className="w-[87%]">
              <div className="pb-12 text-center">
                <h2 className="text-center font-satoshi-bold text-[30px] leading-9 md:text-[36px] md:leading-none">
                  Frequently asked Questions
                </h2>
              </div>
              <Faq faq={cheatSheet.faq} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default CheatSheetPage;

export async function getStaticProps({ params }) {
  const { slug } = params;

  try {
    const { data } = await axios.get(
      `${apiBaseUrl}/api/cheat-sheets?populate=heroImage,author,author.thumbnail,tags,faq,seo,seo.metaImage&filters[slug]=${slug}`
    );

    if (!data?.data) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        cheatSheet: data?.data?.[0]?.attributes || {},
      },
      revalidate: 1800,
    };
  } catch (error) {
    console.log(error);
    return {
      notFound: true,
    };
  }
}

export async function getStaticPaths() {
  const { data: { data } = {} } = await axios.get(
    `${apiBaseUrl}/api/cheat-sheets?pagination[page]=1&pagination[pageSize]=10`
  );

  const paths = data
    ?.filter(({ attributes }) => attributes?.slug)
    ?.map(({ attributes }) => ({
      params: { slug: encodeURIComponent(attributes?.slug || '') },
    }));

  return { paths, fallback: 'blocking' };
}
